import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Appointment, UpdateAppointmentPayload } from '../../models/appointment.model';
import { MatChipsModule } from '@angular/material/chips';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { AppointmentEditFormComponent } from '../appointment-edit-form/appointment-edit-form.component';
import {
  VehicleListComponent,
  VehicleListData,
  EditVehicleData,
} from '../vehicle-list/vehicle-list.component';
import { AddVehicleCardComponent } from '../add-vehicle-card/add-vehicle-card.component';
import { VehicleService } from '../../services/vehicle.service';
import { AppointmentService } from '../../services/appointment.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { CustomerInformationComponent } from '../customer-information/customer-information.component';
import { ProfileService } from '../../services/profile.service';
import { ApiService } from '../../services/api.service';
import { RoleService } from '../../services/role.service';
import { sanitizeErrorMessage, extractErrorMessage } from '../../shared/utils/error.util';
import { formatDuration as formatDurationUtil } from '../../shared/utils/appointment.util';
import { subscribeWithStatusAndData } from '../../shared/utils/api-response.util';
import { computeDropdownPosition } from '../../shared/utils/dropdown-position.util';
import { runVehicleUpdate } from '../../shared/utils/vehicle-edit.util';
import { buildEditingVehicleData } from '../../shared/utils/vehicle-editing-data.util';
import { getUserTimezone } from '../../shared/utils/date.util';

@Component({
  selector: 'app-appointment-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatChipsModule,
    MatRadioModule,
    FormsModule,
    MatButtonModule,
    AppointmentEditFormComponent,
    VehicleListComponent,
    AddVehicleCardComponent,
    CustomerInformationComponent,
  ],
  templateUrl: './appointment-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./appointment-details-modal.component.scss'],
})
export class AppointmentDetailsModalComponent implements OnChanges {
  @Input() appointment: Appointment | null = null;
  @Input() isVisible = false;
  @Output() close = new EventEmitter<void>();
  @Output() appointmentUpdated = new EventEmitter<void>();
  @Output() rescheduleAppointment = new EventEmitter<Appointment>();
  @Output() cancelAppointment = new EventEmitter<Appointment>();
  @Output() markCompleteAppointment = new EventEmitter<Appointment>();

  activeTab = 'booking';
  isEditing = false;

  // Dropdown properties
  showDropdown = false;
  dropdownPosition = { top: 0, left: 0 };

  // Vehicle-related properties
  vehicleData: any[] = [];
  isLoadingVehicles = false;
  selectedVehicleIndex = -1;
  selectedVehicle: any = null;
  isSelectingVehicle = false;

  // Computed property to check if vehicle selection has changed
  get hasVehicleSelectionChanged(): boolean {
    if (!this.appointment || !this.selectedVehicle) {
      return false;
    }
    return this.selectedVehicle.id !== this.appointment.vehicle_id;
  }

  // Computed property to check if edit history should be shown
  /**
   * Determine whether the edit history should be shown for the current appointment.
   * @example
   * shouldShowEditHistory()
   * true
   * @returns {boolean} True if the appointment or its associated vehicle has differing created_at and updated_at timestamps, otherwise false.
   **/
  get shouldShowEditHistory(): boolean {
    if (!this.appointment) return false;

    // Find the vehicle associated with this appointment
    const appointmentVehicle = this.vehicleData.find(
      vehicle => vehicle.id === this.appointment?.vehicle_id
    );

    if (appointmentVehicle) {
      return appointmentVehicle.created_at !== appointmentVehicle.updated_at;
    }
    return this.appointment.created_at !== this.appointment.updated_at;
  }

  // Check if current user is staff (hide edit buttons for staff)
  get isStaffUser(): boolean {
    return this.roleService.isShopFloorEmployee();
  }

  // Check if current user is admin
  get isAdminUser(): boolean {
    return this.roleService.getCurrentUserRole() === 'admin';
  }

  // Edit mode properties
  isEditingVehicle = false;
  showEditingVehicle = false;
  editingVehicleIndex = -1;
  editingVehicleData: EditVehicleData | null = null;

  // Add new vehicle mode properties
  isAddingNewVehicle = false;

  // Store form data for persistence
  vehicleFormData: VehicleListData = {
    selectedState: '',
    licensePlate: '',
    vehicleData: null,
    isVehicleFetched: false,
    vehicleId: '',
  };

  // Edit history data
  editHistory: { action: string; date: Date; type: string }[] = [];

  // Store form references
  @ViewChild('vehicleEditComponent', { static: false }) vehicleEditComponent!: VehicleListComponent;
  @ViewChild('addVehicleComponent', { static: false }) addVehicleComponent!: VehicleListComponent;
  @ViewChild('addVehicleCardComponent', { static: false })
  addVehicleCardComponent!: AddVehicleCardComponent;

  constructor(
    private vehicleService: VehicleService,
    private appointmentService: AppointmentService,
    private messageService: NotificationService,
    private profileService: ProfileService,
    private apiService: ApiService,
    private roleService: RoleService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.showDropdown) {
      const target = event.target as HTMLElement;
      if (!target.closest('.custom-dropdown-menu') && !target.closest('.menu-btn-svg')) {
        this.closeDropdown();
      }
    }
  }

  /**
   * Handle input changes to reset UI state and initialize data when appointment or visibility changes.
   * @example
   * gOnChanges({ isVisible: { currentValue: true }, appointment: { currentValue: {...} } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges map of changed input properties keyed by property name.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible) {
      // Reset to the default tab whenever the modal is opened
      this.activeTab = 'booking';
      this.isEditing = false;
    }

    // Initialize vehicle data when appointment changes
    if (changes['appointment'] && this.appointment) {
      this.initializeVehicleData();
      this.initializeEditHistory();
    }
    this.showEditingVehicle = false;
  }

  onClose() {
    this.close.emit();
  }

  onBackdropClick(event: Event) {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onEdit() {
    this.isEditing = true;
  }

  onCancelAppointmentEdit() {
    this.isEditing = false;
  }

  /**
   * Save appointment edits by sending the provided update payload to the appointment service, updating local state on success, showing success/error messages, exiting edit mode, recording edit history, and emitting an update event to parent.
   * @example
   * nSaveAppointmentEdit({ id: '123', title: 'New Title', startTime: '2026-03-17T10:00:00Z' })
   * undefined
   * @param {{UpdateAppointmentPayload}} {{updatePayload}} - The payload containing appointment fields to update, expected to be in the form produced by the edit form.
   * @returns {{void}} Nothing is returned; the method performs side effects (service call, state updates, UI messages, and event emission).
   **/
  onSaveAppointmentEdit(updatePayload: UpdateAppointmentPayload): void {
    if (!this.appointment) return;

    // The updatePayload is already in the correct format from the edit form

    // Call the appointment service to update the appointment
    this.appointmentService.updateAppointment(this.appointment.id, updatePayload).subscribe({
      next: response => {
        if (response.status) {
          // Update the local appointment data with the response data
          if (response.data) {
            this.appointment = {
              ...this.appointment,
              ...response.data,
            };
          }

          // Update specific display fields that might not be in the response
          // but are needed for the details view
          this.updateDisplayFields(updatePayload);

          this.messageService.add({
            severity: 'success',
            summary: 'Appointment details updated!',
            detail: 'Appointment details have been changed successfully.',
          });

          // Exit edit mode
          this.isEditing = false;

          // Update edit history
          this.addEditHistoryEntry('Edited');

          // Emit event to notify parent component
          this.appointmentUpdated.emit();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: sanitizeErrorMessage(
              response.message || 'Failed to update appointment. Please try again.'
            ),
          });
        }
      },
      error: error => {
        console.error('Error updating appointment:', error);
        const errorMessage = extractErrorMessage(
          error,
          'Failed to update appointment. Please try again.'
        );
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: sanitizeErrorMessage(errorMessage),
        });
      },
    });
  }

  selectTab(tabName: string) {
    this.activeTab = tabName;
  }

  /**
   * Update displayed appointment fields on the component instance based on a partial appointment update payload.
   * @example
   * updateDisplayFields({ location_id: 'loc123', employee_id: 'emp456', appointment_time: '2026-03-17T10:00:00Z', duration: '01:30' })
   * // No return value; appointment fields on the component (branch, employee_name, appointment_time, duration) are updated as side effects.
   * @param {UpdateAppointmentPayload} updatePayload - Payload with optional properties (location_id, employee_id, appointment_time, duration) used to update display fields.
   * @returns {void} No return value; updates component appointment display fields as a side effect.
   **/
  private updateDisplayFields(updatePayload: UpdateAppointmentPayload): void {
    if (!this.appointment) return;

    // Update location/branch display
    if (updatePayload.location_id) {
      // Find the location name from the locations list
      this.profileService.locations$.subscribe((locations: any[]) => {
        const location = locations.find((loc: any) => loc.id === updatePayload.location_id);
        if (location) {
          this.appointment!.branch = location.branch;
        }
      });
    }

    // Update staff/employee display
    if (updatePayload.employee_id && updatePayload.location_id) {
      this.apiService.getEmployeesByLocation(updatePayload.location_id).subscribe(response => {
        if (response.status && Array.isArray(response.data)) {
          const employee = response.data.find((emp: any) => emp.id === updatePayload.employee_id);
          if (employee) {
            this.appointment!.employee_name = `${employee.first_name} ${employee.last_name}`;
          }
        }
      });
    }

    // Update appointment time display
    if (updatePayload.appointment_time) {
      this.appointment.appointment_time = updatePayload.appointment_time;
    }

    // Update duration display
    if (updatePayload.duration) {
      // Convert duration string to the expected format
      const durationFormat = this.parseDurationToHoursAndMinutes(updatePayload.duration);
      this.appointment.duration = durationFormat;
    }
  }

  /**
   * Convert a duration string like "60 minutes" or "1 hour" into an object with hours and/or minutes.
   * @example
   * parseDurationToHoursAndMinutes('90 minutes')
   * { hours: 1, minutes: 30 }
   * @param {{string}} {{durationString}} - Duration string to parse (e.g. "60 minutes" or "1 hour").
   * @returns {{hours?: number; minutes?: number}} Object containing parsed hours and/or minutes (defaults to { minutes: 60 } if unrecognized).
   **/
  private parseDurationToHoursAndMinutes(durationString: string): {
    hours?: number;
    minutes?: number;
  } {
    // Parse duration string like "60 minutes" or "1 hour" to hours and minutes format
    if (durationString.includes('minutes')) {
      const match = durationString.match(/(\d+)\s*minutes?/);
      const totalMinutes = match ? parseInt(match[1], 10) : 60;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return { hours, minutes };
      }
      if (hours > 0) {
        return { hours };
      }
      return { minutes };
    }
    if (durationString.includes('hour')) {
      const match = durationString.match(/(\d+)\s*hours?/);
      const hours = match ? parseInt(match[1], 10) : 1;
      return { hours };
    }
    return { minutes: 60 }; // default
  }

  formatDuration(duration: { minutes?: number; hours?: number } | null | undefined): string {
    return formatDurationUtil(duration);
  }

  formatAppointmentDisplayTime(appointment: Appointment): string {
    return appointment.appointment_time_local
      ? this.formatLocalDateTime(appointment.appointment_time_local)
      : this.formatDateTime(appointment.appointment_time);
  }

  private formatLocalDateTime(dateTimeString: string): string {
    const match = String(dateTimeString).match(/^(\d{4})-(\d{2})-(\d{2})(?:T|\s)(\d{2}):(\d{2})/);
    if (!match) {
      return String(dateTimeString);
    }

    const [, year, month, day, hour, minute] = match;
    return this.formatDateTimeParts(year, month, day, Number(hour), minute);
  }

  private formatDateTime(dateTimeString: string): string {
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) {
      return String(dateTimeString);
    }

    const timeZone = getUserTimezone();
    if (timeZone) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(date)
        .reduce<Record<string, string>>((acc, part) => {
          if (part.type !== 'literal') {
            acc[part.type] = part.value;
          }
          return acc;
        }, {});

      return this.formatDateTimeParts(
        parts['year'],
        parts['month'],
        parts['day'],
        Number(parts['hour']),
        parts['minute']
      );
    }

    return this.formatDateTimeParts(
      String(date.getFullYear()),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
      date.getHours(),
      String(date.getMinutes()).padStart(2, '0')
    );
  }

  private formatDateTimeParts(
    year: string,
    month: string,
    day: string,
    hour24: number,
    minute: string
  ): string {
    const date = new Date(Number(year), Number(month) - 1, Number(day), hour24, Number(minute));
    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
    return `${datePart} | ${timePart}`;
  }

  formatDurationForPayload(duration: { minutes?: number; hours?: number }): string {
    if (!duration) {
      return '60 minutes';
    }

    const { hours, minutes } = duration;
    const totalMinutes = (hours || 0) * 60 + (minutes || 0);

    return `${totalMinutes} minutes`;
  }

  /**
   * Map an appointment status string to a UI severity label.
   * @example
   * etStatusSeverity('pending')
   * 'warn'
   * @param {{string}} {{status}} - Appointment status to convert ('completed', 'confirmed', 'pending', 'cancelled', etc.).
   * @returns {{'success'|'info'|'warn'|'danger'|'secondary'}} Return severity string corresponding to the provided status.
   **/
  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'completed':
        return 'success';
      case 'confirmed':
        return 'info';
      case 'pending':
        return 'warn';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getStatusLabel(status: string): string {
    if (!status) return '';
    const normalized = status.toLowerCase();
    if (normalized === 'cancelled' || normalized === 'canceled') return 'Canceled';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase().trim();
    return normalized ? `status-${normalized}` : '';
  }

  // Vehicle initialization method
  private initializeVehicleData(): void {
    if (this.appointment && this.appointment.customer_id) {
      this.fetchCustomerVehicles(this.appointment.customer_id);
    }
  }

  // Initialize edit history data from vehicle data
  /**
   * Initialize the component's editHistory array from the current appointment and related vehicle data, creating 'Booked' and optional 'Edited' entries with dates and types; falls back to appointment timestamps when no matching vehicle is found.
   * @example
   * initializeEditHistory()
   * undefined
   * @returns {void} No return value.
   */
  private initializeEditHistory(): void {
    if (!this.appointment) return;

    this.editHistory = [];

    // Find the vehicle associated with this appointment
    const appointmentVehicle = this.vehicleData.find(
      vehicle => vehicle.id === this.appointment?.vehicle_id
    );

    if (appointmentVehicle) {
      // Add created entry
      this.editHistory.push({
        action: 'Booked',
        date: new Date(appointmentVehicle.created_at),
        type: 'created',
      });

      // Add edited entry if vehicle was modified after creation
      if (
        appointmentVehicle.updated_at &&
        appointmentVehicle.updated_at !== appointmentVehicle.created_at
      ) {
        this.editHistory.push({
          action: 'Edited',
          date: new Date(appointmentVehicle.updated_at),
          type: 'edited',
        });
      }
    } else {
      // Fallback to appointment data if no vehicle found
      this.editHistory = [
        {
          action: 'Booked',
          date: new Date(this.appointment.created_at),
          type: 'booked',
        },
      ];

      // Add edited entry if appointment was modified after creation
      if (
        this.appointment.updated_at &&
        this.appointment.updated_at !== this.appointment.created_at
      ) {
        this.editHistory.push({
          action: 'Edited',
          date: new Date(this.appointment.updated_at),
          type: 'edited',
        });
      }
    }
  }

  // Add new edit history entry
  private addEditHistoryEntry(action: string): void {
    this.editHistory.push({
      action: action,
      date: new Date(),
      type: action.toLowerCase(),
    });
  }

  // Fetch customer vehicles
  /**
   * Fetches vehicles for a given customer, updates component state (vehicleData, selection, loading), initializes edit history, and handles errors.
   * @example
   * etchCustomerVehicles('customer-123')
   * void
   * @param {{string}} {{customerId}} - Customer identifier used to retrieve associated vehicles.
   * @returns {{void}} Updates component state and does not return a value.
   **/
  fetchCustomerVehicles(customerId: string): void {
    this.isLoadingVehicles = true;
    this.vehicleService.getVehiclesByCustomerId(customerId).subscribe({
      next: response => {
        this.isLoadingVehicles = false;

        if (response.status && response.data) {
          this.vehicleData = response.data;

          // Initialize edit history after vehicle data is loaded
          this.initializeEditHistory();

          // If there's only one vehicle, automatically enter edit mode
          if (this.vehicleData.length === 1) {
            this.editVehicle(this.vehicleData[0], 0);
          } else if (this.vehicleData.length > 0) {
            // Auto-select the first vehicle if available (appointment model doesn't have vehicle_id)
            const vehicleIndex = this.vehicleData.findIndex(
              v => v.id === this.appointment?.vehicle_id
            );
            if (vehicleIndex !== -1) {
              this.selectedVehicleIndex = vehicleIndex;
              this.selectedVehicle = this.vehicleData[vehicleIndex];
            }
          }
        } else {
          this.vehicleData = [];
          // Initialize edit history even if no vehicles found
          this.initializeEditHistory();
        }
      },
      error: error => {
        this.isLoadingVehicles = false;
        console.error('Error fetching vehicles:', error);
        this.vehicleData = [];
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: sanitizeErrorMessage('Failed to fetch vehicle data.'),
        });
      },
    });
  }

  // Vehicle selection methods
  selectVehicle(index: number): void {
    this.selectedVehicleIndex = index;
    this.selectedVehicle = this.vehicleData[index];
  }

  // Handle vehicle selection change from ngModel
  onVehicleSelectionChange(index: number): void {
    if (index !== undefined && index !== null && index >= 0) {
      this.selectedVehicle = this.vehicleData[index];
    }
  }

  editVehicle(vehicle: any, index: number): void {
    this.editingVehicleData = buildEditingVehicleData(vehicle);
    this.editingVehicleIndex = index;
    this.isEditingVehicle = true;
  }

  onOpenVehicleManagement(): void {
    this.showEditingVehicle = true;
    this.isAddingNewVehicle = false;
    this.onCancelEdit();
  }

  onCloseVehicleManagement(): void {
    this.showEditingVehicle = false;
    this.isAddingNewVehicle = false;
    this.onCancelEdit();
  }

  deleteVehicle(vehicle: any, index: number): void {
    // TODO: Implement delete vehicle functionality
    this.messageService.add({
      severity: 'info',
      summary: 'Feature Coming Soon',
      detail: 'Vehicle deletion feature will be available soon.',
    });
  }

  // Vehicle edit mode handlers
  onCancelEdit(): void {
    this.isEditingVehicle = false;
    this.editingVehicleIndex = -1;
    this.editingVehicleData = null;
  }

  /**
   * Save edited vehicle data, update local vehicle list and appointment if applicable, show success message, and trigger related refreshes and events.
   * @example
   * nSaveEdit({ id: '123', licensePlate: 'ABC123', state: 'CA', vehicleData: { vin: '1HGCM82633A004352', make: 'Honda', model: 'Accord', year: 2020 } })
   * undefined
   * @param {{EditVehicleData}} {{updatedData}} - Updated vehicle data payload containing id, licensePlate, state and nested vehicleData (vin, make, model, year).
   * @returns {{void}} Does not return a value; performs updates and side effects (UI messages, events, and data refresh).
   **/
  onSaveEdit(updatedData: EditVehicleData): void {
    if (!this.editingVehicleData || this.editingVehicleIndex < 0) {
      return;
    }
    runVehicleUpdate(this.vehicleService, updatedData, this.messageService, {
      onSuccess: () => {
        if (this.vehicleData && this.vehicleData[this.editingVehicleIndex]) {
          this.vehicleData[this.editingVehicleIndex] = {
            ...this.vehicleData[this.editingVehicleIndex],
            license_plate: updatedData.licensePlate,
            state: updatedData.state,
            vin: updatedData.vehicleData.vin,
            make: updatedData.vehicleData.make,
            model: updatedData.vehicleData.model,
            year: updatedData.vehicleData.year,
          };
          if (this.selectedVehicleIndex === this.editingVehicleIndex) {
            this.selectedVehicle = this.vehicleData[this.editingVehicleIndex];
          }
        }
        const isCurrentAppointmentVehicle =
          Boolean(this.appointment) &&
          Boolean(this.appointment?.vehicle_id) &&
          String(this.appointment?.vehicle_id) === String(updatedData.id);
        if (isCurrentAppointmentVehicle && this.appointment) {
          this.appointment = {
            ...this.appointment,
            vehicle_year: updatedData.vehicleData.year,
            vehicle_make: updatedData.vehicleData.make,
            vehicle_model: updatedData.vehicleData.model,
            vehicle_license_plate: updatedData.licensePlate,
            vehicle_vin: updatedData.vehicleData.vin,
            year: updatedData.vehicleData.year,
            make: updatedData.vehicleData.make,
            model: updatedData.vehicleData.model,
            license_plate: updatedData.licensePlate,
            licensePlate: updatedData.licensePlate,
            vin: updatedData.vehicleData.vin,
          };
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Vehicles details updated!',
          detail: 'The vehicle details have been updated successfully.',
        });
        this.onCloseVehicleManagement();
        this.initializeEditHistory();
        this.appointmentUpdated.emit();
        if (this.appointment && this.appointment.customer_id) {
          this.fetchCustomerVehicles(this.appointment.customer_id);
        }
      },
    });
  }

  // Add new vehicle functionality
  onAddNewVehicle(): void {
    this.isAddingNewVehicle = true;
    this.isEditingVehicle = false;
    this.editingVehicleData = null;
    this.editingVehicleIndex = -1;
  }

  onCancelAddVehicle(): void {
    this.isAddingNewVehicle = false;
  }

  /**
   * Handle a newly added vehicle: add it to the internal list, auto-select it, exit add mode, refresh customer vehicles if applicable, and notify parent components.
   * @example
   * nVehicleAdded({ id: 'v123', make: 'Toyota' })
   * undefined
   * @param {{any}} {{vehicle}} - The vehicle object to add.
   * @returns {{void}} No return value.
   **/
  onVehicleAdded(vehicle: any): void {
    // Add the new vehicle to the vehicle list
    this.vehicleData.push(vehicle);

    // Auto-select the new vehicle
    this.selectVehicle(this.vehicleData.length - 1);

    // Exit add mode
    this.isAddingNewVehicle = false;

    // Refresh the vehicle list to ensure consistency
    if (this.appointment?.customer_id) {
      this.fetchCustomerVehicles(this.appointment.customer_id);
    }

    // Notify parent so appointments table stays in sync.
    this.appointmentUpdated.emit();
  }

  onVehicleCardClosed(): void {
    this.isAddingNewVehicle = false;
  }

  /**
   * Attempt to create and save a new vehicle for the currently loaded appointment's customer. Validates presence of appointment and customer_id, delegates creation to the addVehicleComponent, shows success/error toasts, updates internal vehicle list and selection on success, and refreshes customer vehicles.
   * @example
   * nSaveNewVehicle()
   * void
   * @returns {void} Performs side-effects (displays messages, updates vehicleData, selects the new vehicle, toggles adding state, and fetches customer vehicles); does not return a value.
   **/
  onSaveNewVehicle(): void {
    if (!this.appointment || !this.appointment.customer_id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: sanitizeErrorMessage(
          'Customer ID not found. Please ensure appointment data is loaded.'
        ),
      });
      return;
    }

    // Create vehicle using the add vehicle component
    if (this.addVehicleComponent) {
      subscribeWithStatusAndData(
        this.addVehicleComponent.createVehicleForCustomer(this.appointment.customer_id),
        this.messageService,
        {
          successToast: {
            severity: 'success',
            summary: 'Success',
            detail: 'Vehicle added successfully!',
          },
          errorDetail: 'Failed to add vehicle. Please try again.',
          errorSummary: 'Creation Failed',
          logLabel: 'Error adding vehicle',
          onSuccess: data => {
            this.vehicleData.push(data);
            this.selectVehicle(this.vehicleData.length - 1);
            this.isAddingNewVehicle = false;
            if (this.appointment?.customer_id) {
              this.fetchCustomerVehicles(this.appointment.customer_id);
            }
          },
        }
      );
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: sanitizeErrorMessage('Vehicle component not found. Please try again.'),
      });
    }
  }

  onVehicleFormDataChange(formData: VehicleListData): void {
    this.vehicleFormData = { ...formData };
  }

  // Handle "Select Vehicle" button click
  /**
   * Selects the currently chosen vehicle for the appointment, validates selection, updates the appointment via the appointment service, shows success/error messages, emits an update event, and closes the modal.
   * @example
   * nSelectVehicle()
   * void
   * @returns {void} Does not return a value; performs side effects to update appointment data and UI (messages, event emission, and modal close).
   **/
  onSelectVehicle(): void {
    if (!this.appointment) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: sanitizeErrorMessage('Appointment data not found.'),
      });
      return;
    }

    if (this.selectedVehicleIndex === -1 || !this.selectedVehicle) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: sanitizeErrorMessage('Please select a vehicle first.'),
      });
      return;
    }

    // Set loading state
    this.isSelectingVehicle = true;

    // Prepare the update payload
    const updatePayload: UpdateAppointmentPayload = {
      appointment_status: this.appointment.appointment_status,
      employee_id: this.appointment.employee_id,
      customer_id: this.appointment.customer_id,
      vehicle_id: this.selectedVehicle.id,
      package_id: this.appointment.package_id,
      location_id: this.appointment.location_id,
      appointment_time: this.appointment.appointment_time,
      source: this.appointment.source,
      duration: this.formatDurationForPayload(this.appointment.duration),
    };

    // Call the appointment service to update the appointment
    this.appointmentService.updateAppointment(this.appointment.id, updatePayload).subscribe({
      next: response => {
        this.isSelectingVehicle = false;

        if (response.status) {
          // Update the local appointment data with the response data
          if (response.data) {
            this.appointment = {
              ...this.appointment,
              ...response.data,
            };
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Appointment details updated!',
            detail: 'Appointment details have been successfully confirmed.',
          });

          // Emit event to notify parent component
          this.appointmentUpdated.emit();

          // Close the modal or navigate to next step
          this.onClose();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: sanitizeErrorMessage(
              response.message || 'Failed to update appointment. Please try again.'
            ),
          });
        }
      },
      error: error => {
        this.isSelectingVehicle = false;
        console.error('Error updating appointment with vehicle:', error);
        const errorMessage = extractErrorMessage(
          error,
          'Failed to update appointment. Please try again.'
        );
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: sanitizeErrorMessage(errorMessage),
        });
      },
    });
  }

  // Navigation methods for the form steps
  onContinue(): void {
    // Handle continue action - this would typically move to the next step
    // or complete the current action
  }

  previousStep(): void {
    // Handle going back to previous step
  }

  // Dropdown methods
  /**
   * Toggle the appointment details dropdown: computes its position relative to the event target, shows it, or closes it if already open.
   * @example
   * toggleDropdown(new MouseEvent('click'))
   * undefined
   * @param {Event} event - The DOM event that triggered the toggle; used to determine the target element and to stop propagation.
   * @returns {void} No return value.
   **/
  toggleDropdown(event: Event): void {
    event.stopPropagation();

    if (this.showDropdown) {
      this.closeDropdown();
      return;
    }

    const target = event.currentTarget as HTMLElement;
    requestAnimationFrame(() => {
      this.dropdownPosition = computeDropdownPosition(target);
      this.showDropdown = true;
    });
  }

  closeDropdown(): void {
    this.showDropdown = false;
  }

  markCompleteFromDropdown(): void {
    if (this.appointment) {
      this.closeDropdown();
      this.onClose(); // Close the details modal
      this.markCompleteAppointment.emit(this.appointment);
    }
  }

  rescheduleFromDropdown(): void {
    if (this.appointment) {
      this.closeDropdown();
      this.onClose(); // Close the details modal
      this.rescheduleAppointment.emit(this.appointment);
    }
  }

  cancelFromDropdown(): void {
    if (this.appointment) {
      this.closeDropdown();
      this.cancelAppointment.emit(this.appointment);
      // Parent handles closing the details modal when it shows the cancel confirmation
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
